import { ref, computed } from 'vue'
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  type User,
} from 'firebase/auth'
import { auth } from '@/config/firebase'
import { useToast } from '@/composables/useToast'
import {
  isMfaRequiredError,
  captureMfaResolver,
  completeMfaSignIn,
  clearMfaResolver,
} from '@/composables/useMfa'

// Check if demo mode is enabled
const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true'

const currentUser = ref<User | null>(null)
const isLoading = ref(true)

// True after a correct password when the account has a second factor - the
// login page swaps to its code-entry step until verifyMfaCode() succeeds.
const mfaRequired = ref(false)

// Promise that resolves when Firebase auth is initialized
let authInitializedResolver: (() => void) | null = null
export const authInitialized = new Promise<void>(resolve => {
  authInitializedResolver = resolve
})

// Demo user object that mimics Firebase User structure
const demoFirebaseUser = {
  uid: 'demo-user-001',
  email: 'demo@turtledovecyber.com',
  emailVerified: true,
  displayName: 'Demo User',
  photoURL: null,
  phoneNumber: null,
  isAnonymous: false,
  tenantId: null,
  providerData: [],
  metadata: {
    creationTime: '2024-01-01T00:00:00Z',
    lastSignInTime: new Date().toISOString(),
  },
  refreshToken: 'demo-refresh-token',
  getIdToken: async () => 'demo-id-token',
  getIdTokenResult: async () => ({
    token: 'demo-id-token',
    claims: { role: 'org_admin' },
    authTime: new Date().toISOString(),
    issuedAtTime: new Date().toISOString(),
    expirationTime: new Date(Date.now() + 3600000).toISOString(),
    signInProvider: 'password',
    signInSecondFactor: null,
  }),
  reload: async () => {},
  toJSON: () => ({ uid: 'demo-user-001', email: 'demo@turtledovecyber.com' }),
  delete: async () => {},
} as unknown as User

// Initialize auth state - demo mode auto-authenticates
if (isDemoMode) {
  // In demo mode, auto-authenticate with demo user
  setTimeout(() => {
    currentUser.value = demoFirebaseUser
    isLoading.value = false
    if (authInitializedResolver) {
      authInitializedResolver()
      authInitializedResolver = null
    }
  }, 100)
} else {
  // Production mode - use Firebase auth state listener
  onAuthStateChanged(auth, user => {
    currentUser.value = user
    isLoading.value = false

    // Resolve the initialization promise on first auth state change
    if (authInitializedResolver) {
      authInitializedResolver()
      authInitializedResolver = null
    }
  })
}

export function useAuth() {
  const toast = useToast()
  const isAuthenticated = computed(() => !!currentUser.value)

  /**
   * Sign in with email + password.
   *
   * Returns the User on success. Returns null when the account has a second
   * factor: `mfaRequired` flips true and the caller must collect a code and
   * call verifyMfaCode(). Any other failure throws (toast already shown).
   */
  async function login(email: string, password: string): Promise<User | null> {
    // Demo mode: accept any credentials and return demo user
    if (isDemoMode) {
      await new Promise(resolve => setTimeout(resolve, 500)) // Simulate network delay
      currentUser.value = demoFirebaseUser
      mfaRequired.value = false
      return demoFirebaseUser
    }

    try {
      const credential = await signInWithEmailAndPassword(auth, email, password)
      currentUser.value = credential.user
      mfaRequired.value = false
      return credential.user
    } catch (error: any) {
      if (isMfaRequiredError(error)) {
        // Password was correct - hand off to the second-factor step.
        captureMfaResolver(error)
        mfaRequired.value = true
        return null
      }
      // Show error toast
      toast.error(error, 'Failed to sign in. Please check your credentials.')
      // Re-throw so component can handle UI state (loading, etc.)
      throw error
    }
  }

  /** Complete a sign-in that was challenged for a second factor. */
  async function verifyMfaCode(code: string): Promise<User> {
    try {
      const user = await completeMfaSignIn(code)
      currentUser.value = user
      mfaRequired.value = false
      return user
    } catch (error: any) {
      toast.error(error, 'Invalid verification code. Please try again.')
      throw error
    }
  }

  /** Abandon a pending second-factor challenge (user hit "back"). */
  function cancelMfa() {
    clearMfaResolver()
    mfaRequired.value = false
  }

  async function logout() {
    // Demo mode: just clear the user
    if (isDemoMode) {
      currentUser.value = null
      return
    }

    try {
      await signOut(auth)
      currentUser.value = null
      cancelMfa()
    } catch (error: any) {
      toast.error(error, 'Failed to sign out')
      throw error
    }
  }

  return {
    currentUser: computed(() => currentUser.value),
    isAuthenticated,
    isLoading: computed(() => isLoading.value),
    mfaRequired: computed(() => mfaRequired.value),
    login,
    verifyMfaCode,
    cancelMfa,
    logout,
  }
}
